import { makeAutoObservable } from 'mobx';
import {
    apiGamprotectStatusGetRequest,
    ApiGamprotectStatusGetResponse200Type,
    Response500Type,
} from 'src/api_openapi/v2/gamprotect/apiGamprotectStatusGet';
import { Common } from 'src/AppState/Common';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { GamprotectRequestSuccessDataType, sortMarkersByStatus } from './convertCustomerDataForGamprotect';
import {
    apiGamprotectSubmitRequest,
    ApiGamprotectSubmitResponseType,
} from 'src/api_openapi/v2/gamprotect/apiGamprotectSubmit';
import {
    apiGamprotectRemoveRequest,
    ApiGamprotectRemoveResponseType,
} from 'src/api_openapi/v2/gamprotect/apiGamprotectRemove';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { MarkerType } from './types';

const filterActiveMarkers = (markers: Array<MarkerType>): Array<MarkerType> => {
    return markers.filter((marker) => marker.active === true);
};
type ApiGamprotectStatusGetResponse500Type = Response500Type;

type ApiGamprotectResponseType =
    | {
          status: 'success';
          body: ApiGamprotectStatusGetResponse200Type;
      }
    | {
          status: 'error';
          body: ApiGamprotectStatusGetResponse500Type;
      };

export class GamprotectUserStore {
    private readonly gamprotectResource: Resource<ApiGamprotectResponseType | null>;
    private readonly configState: ConfigState;

    public constructor(
        private readonly common: Common,
        account_id: number
    ) {
        this.gamprotectResource = new Resource(async (): Promise<ApiGamprotectResponseType | null> => {
            const response = await apiGamprotectStatusGetRequest(
                this.common.appEnvParams.gatewayUrl,
                {
                    account_id,
                },
                {
                    authorization: `Bearer ${this.common.session.currentJwt}`,
                }
            );
            if (response.status === 200) {
                return {
                    status: 'success',
                    body: response.body,
                };
            }
            if (response.status === 500) {
                return {
                    status: 'error',
                    body: response.body,
                };
            }

            return null;
        });

        this.configState = ConfigState.get(common);

        makeAutoObservable(this);
    }

    private get data(): ApiGamprotectStatusGetResponse200Type | 'loading' | 'error' {
        const data = this.gamprotectResource.get();

        if (data.type === 'error') {
            return 'error';
        }

        if (data.type === 'loading') {
            return 'loading';
        }

        if (data.value !== null && data.value.status === 'success') {
            return data.value.body;
        }

        return 'error';
    }

    public get markers(): Array<MarkerType> | 'loading' | 'error' {
        const data = this.data;

        if (data === 'error') {
            return 'error';
        }

        if (data === 'loading') {
            return 'loading';
        }

        const output: Array<MarkerType> = [];

        for (const marker of data.gamprotect.markers) {
            const isActive = marker.lastSeen === undefined;

            output.push({
                active: isActive,
                ...marker,
            });
        }

        return sortMarkersByStatus(output);
    }

    // To check if there is gamprotect added by current brand please use `hasGamprotectOnMyBrand`
    public get hasGamprotect(): boolean | null {
        if (this.markers === 'error' || this.markers === 'loading') {
            return null;
        }

        const activeMarkers = filterActiveMarkers(this.markers);

        if (activeMarkers.length > 0) {
            return true;
        }

        return false;
    }

    public get hasGamprotectOnMyBrand(): boolean | null {
        const gamprotectConfig = this.configState.config.gamprotect;

        if (gamprotectConfig === false) {
            return null;
        }

        if (this.markers === 'error' || this.markers === 'loading') {
            return null;
        }

        const activeMarkers = filterActiveMarkers(this.markers);

        if (activeMarkers.length === 0) {
            return false;
        }

        if (activeMarkers.find((marker) => marker.brand === gamprotectConfig.brand) !== undefined) {
            return true;
        }

        return false;
    }

    public refreshData = (): Promise<void> => {
        return this.gamprotectResource.refresh();
    };

    public handleSubmit = async (
        gamprotectData: GamprotectRequestSuccessDataType,
        inputText: string
    ): Promise<ApiGamprotectSubmitResponseType> => {
        return await apiGamprotectSubmitRequest(
            this.common.appEnvParams.gatewayUrl,
            {
                requestBody: {
                    ...gamprotectData.data,
                    entryReason: inputText,
                },
            },
            {
                authorization: `Bearer ${this.common.session.currentJwt}`,
            }
        );
    };

    public handleRemove = async (
        gamprotectData: GamprotectRequestSuccessDataType,
        inputText: string
    ): Promise<ApiGamprotectRemoveResponseType> => {
        return await apiGamprotectRemoveRequest(
            this.common.appEnvParams.gatewayUrl,
            {
                requestBody: {
                    ...gamprotectData.data,
                    removeReason: inputText,
                },
            },
            {
                authorization: `Bearer ${this.common.session.currentJwt}`,
            }
        );
    };
}
